// FLEET LIVE's vocabulary (#6777, epic #6552): which tasks are items on the deck, which
// of the 8 stages each sits at, what state it is in, which agent bucket a live task
// counts in, and the busiest-machine order. PURE: `packages/db` reads the rows these
// tables classify (`queries/fleet-live.ts` and its siblings), `apps/api` `routes/fleet-live.ts` assembles
// the body (`FleetLiveWireSchema`, `../wire/fleet-live.ts`).
//
// Every table is `satisfies Record<…>` over a closed vocabulary, so a new task status,
// raw-task status, origin or blocker kind fails the build here until it is placed. That
// is the point: the area-05 `paused` status, and #6759's `triage_judge` / `kb_fold`
// origins, cannot reach the deck unclassified.
//
// The SQL in `packages/db` reads its status and origin lists OFF these tables
// (`FLEET_LIVE_ITEM_ORIGINS`, `FLEET_LIVE_DECK_TASK_STATUSES`), the
// `TASK_DEP_SATISFIED_STATUSES` posture, so the bounded item list and the counts filter
// exactly the rows `stageOf` can place.

import type { BlockerKind } from '../entities/blocker';
import { RawTaskStatus } from '../entities/raw-task/status';
import { TaskStatus } from '../entities/task/status';
import { TaskOrigin } from './task-envelope/origin';

/** The 8 stages, in pipeline order — exactly these strings (the Fleet Live canvas labels). */
export const FLEET_LIVE_STAGES = [
  'Ask',
  'Triage',
  'Plan',
  'Picked up',
  'Pull request',
  'Fixed until green',
  'Merged',
  'Shipped',
] as const;
export type FleetLiveStage = (typeof FLEET_LIVE_STAGES)[number];

/** Not on the deck: the row is not an item, or its stage has nothing to show. */
export type OffDeck = 'off_deck';

/** An item's state inside its stage. */
export const FLEET_LIVE_STATES = ['running', 'waiting', 'parked', 'blocked', 'set_aside'] as const;
export type FleetLiveState = (typeof FLEET_LIVE_STATES)[number];

/**
 * Each origin's role. `item`: the task IS a deck item. `follow_up`: it works on a PR
 * that belongs to an item (a review, a babysit round), so it attributes to that PR's item
 * and is never an item of its own. `excluded`: no stage path — `setup` onboards a repo,
 * `devops` reports and acts on infrastructure and opens no PR (`origin.ts`, the `Devops`
 * docblock), and `triage_judge` / `kb_fold` are one model call each (#6759). Its live runs still count in `agents.total` (bucket `none`).
 */
export const FLEET_LIVE_ORIGIN_ROLE = {
  [TaskOrigin.Conversation]: 'item',
  [TaskOrigin.GhIssue]: 'item',
  [TaskOrigin.Sentry]: 'item',
  [TaskOrigin.Linear]: 'item',
  [TaskOrigin.Manual]: 'item',
  // Only while not terminal, at Plan — `stageOf` places it.
  [TaskOrigin.Scout]: 'item',
  [TaskOrigin.PrReview]: 'follow_up',
  [TaskOrigin.PrReviewReply]: 'follow_up',
  [TaskOrigin.PrBabysit]: 'follow_up',
  [TaskOrigin.PrBabysitWatch]: 'follow_up',
  [TaskOrigin.Setup]: 'excluded',
  [TaskOrigin.Devops]: 'excluded',
  // #6759: one model call offered to a machine (no checkout, no PR). Its caller's own work
  // (the triage batch, the KB fold) is what the deck shows, never the call.
  [TaskOrigin.TriageJudge]: 'excluded',
  [TaskOrigin.KbFold]: 'excluded',
  [TaskOrigin.PlanAuthor]: 'excluded',
} as const satisfies Record<TaskOrigin, 'item' | 'follow_up' | 'excluded'>;

/** The origins whose tasks are deck items — the list the item and count reads filter on. */
export const FLEET_LIVE_ITEM_ORIGINS: readonly TaskOrigin[] = Object.values(TaskOrigin).filter(
  (o) => FLEET_LIVE_ORIGIN_ROLE[o] === 'item',
);

/**
 * Each task status's stage, before `stageOf`'s two refinements (`completed` with no PR
 * leaves the deck; `merged` with a recorded success deploy is lifted to `Shipped`).
 * `pending` sits at Plan, waiting for a free machine.
 */
export const FLEET_LIVE_TASK_STAGE = {
  [TaskStatus.Pending]: 'Plan',
  [TaskStatus.Assigned]: 'Picked up',
  [TaskStatus.InProgress]: 'Picked up',
  [TaskStatus.Completed]: 'Pull request',
  [TaskStatus.AwaitingCi]: 'Fixed until green',
  [TaskStatus.AwaitingReview]: 'Pull request',
  [TaskStatus.Mergeable]: 'Pull request',
  [TaskStatus.Merged]: 'Merged',
  [TaskStatus.Failed]: 'off_deck',
  [TaskStatus.Cancelled]: 'off_deck',
  // #6747: the task holds its machine, the stage `assigned` / `in_progress` sit at.
  [TaskStatus.Paused]: 'Picked up',
} as const satisfies Record<TaskStatus, FleetLiveStage | OffDeck>;

/** The task statuses that can place an item on the deck — the item and count reads' list. */
export const FLEET_LIVE_DECK_TASK_STATUSES: readonly TaskStatus[] = Object.values(
  TaskStatus,
).filter((s) => FLEET_LIVE_TASK_STAGE[s] !== 'off_deck');

/** The stage of one task, or `off_deck`. */
export function stageOf(input: {
  origin: TaskOrigin;
  status: TaskStatus;
  pullRequestNumber: number | null;
  /** A recorded successful deploy verdict exists for the PR's merge commit. */
  shipped: boolean;
}): FleetLiveStage | OffDeck {
  if (FLEET_LIVE_ORIGIN_ROLE[input.origin] !== 'item') return 'off_deck';
  const stage: FleetLiveStage | OffDeck = FLEET_LIVE_TASK_STAGE[input.status];
  if (stage === 'off_deck') return stage;
  // FIRST, before the scout rule: a report-outcome run (scout, a `gaps` pass) finishes
  // `completed` with nothing left to merge (the `isDeadOnboardingPass` precedent). Taken
  // after it, every completed scout would sit at Plan forever.
  if (input.status === TaskStatus.Completed && input.pullRequestNumber === null) return 'off_deck';
  if (input.origin === TaskOrigin.Scout) return input.status === TaskStatus.Merged ? stage : 'Plan';
  if (stage === 'Merged' && input.shipped) return 'Shipped';
  return stage;
}

/**
 * An open blocker's state: `parked` waits on a person's judgement, `blocked` cannot
 * proceed until something is supplied or reachable.
 */
export const FLEET_LIVE_BLOCKER_STATE = {
  decision_needed: 'parked',
  missing_credential: 'blocked',
  unreachable_service: 'blocked',
} as const satisfies Record<BlockerKind, 'parked' | 'blocked'>;

/**
 * A task item's state: its open blocker's, else `waiting` while pending, `parked` while
 * paused (#6747: it waits on a person, the meaning `parked` has for a `decision_needed`
 * blocker), else `running`.
 */
export function taskStateOf(input: {
  status: TaskStatus;
  blockerKind: BlockerKind | null;
}): FleetLiveState {
  if (input.blockerKind !== null) return FLEET_LIVE_BLOCKER_STATE[input.blockerKind];
  if (input.status === TaskStatus.Paused) return 'parked';
  return input.status === TaskStatus.Pending ? 'waiting' : 'running';
}

/** How long a dropped ask stays visible at Triage, set aside. */
export const FLEET_LIVE_DROPPED_VISIBLE_MS = 60 * 60_000;

/**
 * Each raw-task status's place. `triaged` is Triage only while no verdict is recorded
 * (the claim state, `raw_tasks.status` docblock); with a verdict the tasks it minted are
 * the items. `dropped` is Triage, set aside, for {@link FLEET_LIVE_DROPPED_VISIBLE_MS}
 * after it settled. `escalated` waits on a human on the asks page, off the deck.
 */
export const FLEET_LIVE_RAW_TASK_STAGE = {
  [RawTaskStatus.Pending]: { stage: 'Ask', state: 'waiting' },
  [RawTaskStatus.Triaged]: 'triage_while_unsettled',
  [RawTaskStatus.AwaitingInfo]: { stage: 'Triage', state: 'parked' },
  [RawTaskStatus.Escalated]: 'off_deck',
  [RawTaskStatus.Dropped]: 'set_aside_while_recent',
} as const satisfies Record<
  RawTaskStatus,
  | { stage: FleetLiveStage; state: FleetLiveState }
  | OffDeck
  | 'triage_while_unsettled'
  | 'set_aside_while_recent'
>;

/** The raw-task statuses that can place an ask on the deck — the raw reads' list. */
export const FLEET_LIVE_DECK_RAW_TASK_STATUSES: readonly RawTaskStatus[] = Object.values(
  RawTaskStatus,
).filter((s) => FLEET_LIVE_RAW_TASK_STAGE[s] !== 'off_deck');

/** The place of one raw task, or `off_deck`. */
export function rawStageOf(input: {
  status: RawTaskStatus;
  hasVerdict: boolean;
  /**
   * `raw_tasks.settled_at` is within {@link FLEET_LIVE_DROPPED_VISIBLE_MS} of the read's
   * clock — decided by the read (its bound is computed from the caller's `nowMs`), so REST
   * and the stream place the same ask the same way.
   */
  settledRecently: boolean;
}): { stage: FleetLiveStage; state: FleetLiveState } | OffDeck {
  const place = FLEET_LIVE_RAW_TASK_STAGE[input.status];
  if (place === 'triage_while_unsettled') {
    return input.hasVerdict ? 'off_deck' : { stage: 'Triage', state: 'running' };
  }
  if (place === 'set_aside_while_recent') {
    return input.settledRecently ? { stage: 'Triage', state: 'set_aside' } : 'off_deck';
  }
  return place;
}

/**
 * A babysit round that fixes CI. `fix_ci` is RETIRED (#3883, `BABYSIT_SPEC_ACTIONS`): the
 * mint writes a `fix_pr` instead, and a `fix_pr` carrying a failed check is the CI round
 * (the `ciRound` test in `BabysitTaskSpecSchema`'s refine). Stored `fix_ci` rows still
 * count. Prod 2026-10-28, `pr_babysit` tasks created in 30 days: 0 `fix_ci`, 531 `fix_pr`
 * with failed checks, 1,410 `fix_pr` with none. Mirrored in SQL by `ciFixRoundSql`
 * (`packages/db/src/queries/fleet-live-sql.ts`).
 */
export function isCiFixRound(spec: {
  action: string;
  failed_checks?: readonly string[] | undefined;
}): boolean {
  if (spec.action === 'fix_ci') return true;
  return spec.action === 'fix_pr' && (spec.failed_checks ?? []).length > 0;
}

/**
 * Which agent bucket a live task counts in. `fixing_if_fix_ci`: a babysit round counts as
 * `fixing` only when it is a CI fix round ({@link isCiFixRound}), else `none`. `none`
 * counts in `agents.total` only.
 */
export const FLEET_LIVE_AGENT_BUCKET = {
  [TaskOrigin.Conversation]: 'writing',
  [TaskOrigin.GhIssue]: 'writing',
  [TaskOrigin.Sentry]: 'writing',
  [TaskOrigin.Linear]: 'writing',
  [TaskOrigin.Manual]: 'writing',
  [TaskOrigin.PrReview]: 'reviewing',
  [TaskOrigin.PrReviewReply]: 'reviewing',
  [TaskOrigin.PrBabysit]: 'fixing_if_fix_ci',
  [TaskOrigin.PrBabysitWatch]: 'none',
  [TaskOrigin.Scout]: 'none',
  [TaskOrigin.Setup]: 'none',
  [TaskOrigin.Devops]: 'none',
  // #6759: a model call writes, reviews and fixes nothing; it counts in `agents.total` only.
  [TaskOrigin.TriageJudge]: 'none',
  [TaskOrigin.KbFold]: 'none',
  [TaskOrigin.PlanAuthor]: 'none',
} as const satisfies Record<TaskOrigin, 'writing' | 'reviewing' | 'fixing_if_fix_ci' | 'none'>;

export type FleetLiveAgentBucket = 'writing' | 'reviewing' | 'fixing' | 'none';

/** The bucket of one live task. */
export function fleetLiveAgentBucket(
  origin: TaskOrigin,
  ciFixRound: boolean,
): FleetLiveAgentBucket {
  const bucket = FLEET_LIVE_AGENT_BUCKET[origin];
  if (bucket === 'fixing_if_fix_ci') return ciFixRound ? 'fixing' : 'none';
  return bucket;
}

/** A machine as the busiest order reads it. `cpuPct: null` is a cpu reading not reported. */
export type BusiestMachineKey = { name: string; jobs: number; cpuPct: number | null };

/**
 * Busiest first: machines holding at least one job, then CPU descending (a machine with
 * no CPU reading after every machine with one), then name.
 */
export function compareBusiestMachines(a: BusiestMachineKey, b: BusiestMachineKey): number {
  const working = Number(b.jobs > 0) - Number(a.jobs > 0);
  if (working !== 0) return working;
  if (a.cpuPct === null || b.cpuPct === null) {
    const unreported = Number(a.cpuPct === null) - Number(b.cpuPct === null);
    if (unreported !== 0) return unreported;
  } else if (a.cpuPct !== b.cpuPct) {
    return b.cpuPct - a.cpuPct;
  }
  return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
}

/** A machine's label: its name plus the first 8 characters of its id, never its hostname. */
export function fleetLiveMachineLabel(name: string, runnerId: string): string {
  return `${name} · ${runnerId.slice(0, 8)}`;
}
